import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ObservationsField from "../../../components/ObservationsField";
import { clientService } from "../../../Services/client.service";
import { getCompanyIdCookie } from "../../../Utils/companyIdCookie";

// Aba "Observações" do cliente.
// Leitura: ClientNotes/OperationalNotes/BillingNotes vêm no GET
// /customers/{id} (confirmado pelo Luís), então carregamos direto daqui.
//
// Gravação: PUT /customers/{id} não aceita atualização parcial — exige o
// payload completo do cliente (confirmado via swagger + erros 400 reais).
// Por isso handleSave busca o cliente inteiro primeiro e remonta o payload
// completo, trocando só o campo de observação que foi editado.
//
// Nomes de campo do payload (GET -> PUT) confirmados via swagger:
// ClientName -> CompanyName, ClientTradeName -> TradeName, Doc -> Document,
// SalespersonId -> SellerId, ClientNotes -> Observation,
// OperationalNotes -> OperationalObservation, BillingNotes -> BillingObservation,
// SimpleOption -> SimpleTax, Phone1 -> PhoneNumber, Phone2 -> SecondaryPhoneNumber,
// ResponsibleEmail -> Email.
//
// PENDENTE: RegionalId, MasterUserEmail, MasterUserName e
// MasterUserAccessLevelId são obrigatórios no swagger mas não vêm no GET
// nem existe origem confirmada pra eles ainda — mandados como null.
export default function Notes({ clientId }) {
  const { t } = useTranslation();

  const [clientNotes, setClientNotes] = useState("");
  const [operationalNotes, setOperationalNotes] = useState("");
  const [billingNotes, setBillingNotes] = useState("");
  const [billingEmails, setBillingEmails] = useState("");

  const [editingClient, setEditingClient] = useState(false);
  const [editingOperational, setEditingOperational] = useState(false);
  const [editingBilling, setEditingBilling] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadNotes() {
      if (!clientId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setLoadError(false);

      const client = await clientService.fetchClient(clientId);

      if (!isMounted) return;

      if (!client) {
        setLoadError(true);
        setLoading(false);
        return;
      }

      setClientNotes(client.clientNotes ?? "");
      setOperationalNotes(client.operationalNotes ?? "");
      setBillingNotes(client.billingNotes ?? "");
      setLoading(false);
    }

    loadNotes();

    return () => {
      isMounted = false;
    };
  }, [clientId]);

  // field é um dos: "clientNotes" | "operationalNotes" | "billingNotes"
  // (chaves do GET, usadas só pra saber qual observação foi editada agora).
  const handleSave = async (field, value, setEditing) => {
    const fullClient = await clientService.fetchClient(clientId);
    if (!fullClient) {
      return;
    }

    const payload = {
      RegionalId: getCompanyIdCookie(),
      CompanyName: fullClient.clientName ?? null,
      TradeName: fullClient.clientTradeName ?? null,
      Doc: fullClient.doc ?? null,
      RepresentativeId: fullClient.representativeId ?? null,
      SellerId: fullClient.salespersonId ?? null,
      ServicePlanId: fullClient.servicePlanId ?? null,
      MasterUserEmail: null,
      MasterUserName: null,
      MasterUserAccessLevelId: null,
      StateRegistration: fullClient.stateRegistration ?? null,
      MunicipalRegistration: fullClient.municipalRegistration ?? null,
      SimpleTax: fullClient.simpleOption ? "S" : "N",
      Public: fullClient.publicEntity ?? null,
      Email: fullClient.responsibleEmail ?? null,
      PhoneNumber: fullClient.phone1 ?? null,
      SecondaryPhoneNumber: fullClient.phone2 ?? null,
      Observation: field === "clientNotes" ? value : fullClient.clientNotes ?? null,
      OperationalObservation:
        field === "operationalNotes" ? value : fullClient.operationalNotes ?? null,
      BillingObservation: field === "billingNotes" ? value : fullClient.billingNotes ?? null,
      BirthDate: fullClient.birthDate ?? null,
      PersonType: fullClient.personType ?? null,
      Dda: fullClient.dda ?? null,
      CreditAnalysis: fullClient.creditAnalysis ?? null,
      CreditAnalysisDate: fullClient.creditAnalysisDate ?? null,
      PaymentBook: fullClient.paymentBook ?? null,
      AdministrativeRestrictionId: fullClient.administrativeRestrictionId ?? null,
      AdministrativeRestrictionDate: fullClient.administrativeRestrictionDate ?? null,
      AdministrativeRestrictionEndDate: fullClient.administrativeRestrictionEndDate ?? null,
      LastTemporarySuspensionDate: fullClient.lastTemporarySuspensionDate ?? null,
      AuthorizeEmail: fullClient.authorizeEmail ?? null,
      AuthorizeSms: fullClient.authorizeSms ?? null,
      AuthorizeNegativeList: fullClient.authorizeNegativeList ?? null,
      AuthorizeAutomaticRestriction: fullClient.authorizeAutomaticRestriction ?? null,
      Whatsapp: fullClient.whatsapp ?? null,
      ZipCode: fullClient.postalCode1 ?? null,
      Street: fullClient.address1 ?? null,
      Number: fullClient.number1 ?? null,
      Neighborhood: fullClient.district1 ?? null,
      Complement: fullClient.complement1 ?? null,
      City: fullClient.city1 ?? null,
      State: fullClient.state1 ?? null,
      SecondaryZipCode: fullClient.postalCode2 ?? null,
      SecondaryStreet: fullClient.address2 ?? null,
      SecondaryNumber: fullClient.number2 ?? null,
      SecondaryNeighborhood: fullClient.district2 ?? null,
      SecondaryComplement: fullClient.complement2 ?? null,
      SecondaryCity: fullClient.city2 ?? null,
      SecondaryState: fullClient.state2 ?? null,
    };

    const result = await clientService.updateClient(clientId, payload);
    if (!result || result.error) {
      return;
    }
    setEditing(false);
  };

  if (loading) {
    return (
      <div className="p-3 text-muted">
        {t("CUSTOMER.NOTES.LOADING")}
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-3 text-danger">
        {t("CUSTOMER.NOTES.LOAD_ERROR")}
      </div>
    );
  }

  return (
    <div className="p-3">
      {/* Observações do Cliente */}
      <div className="mb-4">
        <div className="bg-dark text-white px-3 py-2 mb-3">
          {t("CUSTOMER.NOTES.CLIENT_TITLE")}
        </div>
        <ObservationsField
          label={t("CUSTOMER.NOTES.CLIENT_LABEL")}
          value={clientNotes}
          onChange={setClientNotes}
          placeholder={t("CUSTOMER.NOTES.CLIENT_PLACEHOLDER")}
          disabled={!editingClient}
        />
        <div className="d-flex justify-content-end gap-2 mt-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setEditingClient(true)}
          >
            {t("CUSTOMER.NOTES.EDIT")}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() =>
              handleSave("clientNotes", clientNotes, setEditingClient)
            }
          >
            {t("CUSTOMER.NOTES.SAVE")}
          </button>
        </div>
      </div>

      {/* Observações Operacionais */}
      <div className="mb-4">
        <div className="bg-primary text-white px-3 py-2 mb-3">
          {t("CUSTOMER.NOTES.OPERATIONAL_TITLE")}
        </div>
        <ObservationsField
          label=""
          value={operationalNotes}
          onChange={setOperationalNotes}
          placeholder={t("CUSTOMER.NOTES.OPERATIONAL_PLACEHOLDER")}
          disabled={!editingOperational}
        />
        <div className="d-flex justify-content-end gap-2 mt-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setEditingOperational(true)}
          >
            {t("CUSTOMER.NOTES.EDIT")}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() =>
              handleSave("operationalNotes", operationalNotes, setEditingOperational)
            }
          >
            {t("CUSTOMER.NOTES.SAVE")}
          </button>
        </div>
      </div>

      {/* Observações de Faturamento */}
      <div className="mb-3">
        <div className="bg-primary text-white px-3 py-2 mb-3">
          {t("CUSTOMER.NOTES.BILLING_TITLE")}
        </div>
        <ObservationsField
          label=""
          value={billingNotes}
          onChange={setBillingNotes}
          placeholder={t("CUSTOMER.NOTES.BILLING_PLACEHOLDER")}
          disabled={!editingBilling}
        />

        <div className="mt-3">
          <label className="form-label">
            {t("CUSTOMER.NOTES.BILLING_EMAILS_LABEL")}
          </label>
          <textarea
            className="form-control"
            rows={2}
            value={billingEmails}
            onChange={(e) => setBillingEmails(e.target.value)}
            placeholder={t("CUSTOMER.NOTES.BILLING_EMAILS_PLACEHOLDER")}
            disabled={!editingBilling}
          />
        </div>

        <div className="d-flex justify-content-end gap-2 mt-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setEditingBilling(true)}
          >
            {t("CUSTOMER.NOTES.EDIT")}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() =>
              handleSave("billingNotes", billingNotes, setEditingBilling)
            }
          >
            {t("CUSTOMER.NOTES.SAVE")}
          </button>
        </div>
      </div>
    </div>
  );
}
